import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getAdminMilestones, createAdminMilestone, updateAdminMilestone, deleteAdminMilestone, BackendMilestone } from '@/services/admin/milestones.admin.api';

interface MilestoneFormData {
  year: string;
  title_zh: string;
  title_en: string;
  desc_zh: string;
  desc_en: string;
  icon: string;
  sort_order: number;
}

const ICON_OPTIONS = [
  'ri-briefcase-line',
  'ri-global-line',
  'ri-community-line',
  'ri-customer-service-line',
  'ri-award-line',
  'ri-group-line',
  'ri-hand-heart-line',
  'ri-seedling-line',
  'ri-rocket-line',
  'ri-lightbulb-line',
];

export default function AdminMilestonesPage() {
  const { t } = useTranslation();
  const [milestones, setMilestones] = useState<BackendMilestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<MilestoneFormData>({
    year: '',
    title_zh: '',
    title_en: '',
    desc_zh: '',
    desc_en: '',
    icon: 'ri-briefcase-line',
    sort_order: 0,
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMilestones();
  }, []);

  const fetchMilestones = async () => {
    try {
      setLoading(true);
      const data = await getAdminMilestones();
      setMilestones(data || []);
    } catch (err) {
      setError('获取里程碑列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.year || !formData.title_zh) {
      setError('请填写年份和中文标题');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const payload = {
        year: formData.year,
        title_zh: formData.title_zh,
        title_en: formData.title_en || '',
        desc_zh: formData.desc_zh || '',
        desc_en: formData.desc_en || '',
        icon: formData.icon,
        sort_order: formData.sort_order,
      };

      if (editingId) {
        await updateAdminMilestone(editingId, payload);
      } else {
        await createAdminMilestone(payload);
      }

      setShowModal(false);
      resetForm();
      fetchMilestones();
    } catch (err) {
      setError(editingId ? '更新失败' : '创建失败');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (milestone: BackendMilestone) => {
    setEditingId(milestone.id!);
    setFormData({
      year: milestone.year,
      title_zh: milestone.title_zh,
      title_en: milestone.title_en || '',
      desc_zh: milestone.desc_zh || '',
      desc_en: milestone.desc_en || '',
      icon: milestone.icon || 'ri-briefcase-line',
      sort_order: milestone.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个里程碑吗？')) return;

    try {
      await deleteAdminMilestone(id);
      fetchMilestones();
    } catch (err) {
      alert('删除失败');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      year: '',
      title_zh: '',
      title_en: '',
      desc_zh: '',
      desc_en: '',
      icon: 'ri-briefcase-line',
      sort_order: 0,
    });
    setError('');
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  return (
    <div className="p-8">
      <div className="max-w-6xl mx-auto">
            {/* Header */}
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">成长足迹管理</h1>
                <p className="text-sm text-gray-500 mt-1">管理GTRA的重要里程碑和发展历程</p>
              </div>
              <button
                onClick={openCreateModal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                添加里程碑
              </button>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Loading State */}
            {loading ? (
              <div className="text-center py-12 text-gray-500">加载中...</div>
            ) : (
              /* Milestones Table */
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        年份
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        标题
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        图标
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        排序
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        操作
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {milestones.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                          暂无里程碑数据
                        </td>
                      </tr>
                    ) : (
                      milestones.map((milestone) => (
                        <tr key={milestone.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {milestone.year}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{milestone.title_zh}</div>
                            {milestone.title_en && (
                              <div className="text-sm text-gray-500">{milestone.title_en}</div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <i className={`${milestone.icon || 'ri-briefcase-line'} text-xl text-gray-600`} />
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {milestone.sort_order || 0}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleEdit(milestone)}
                              className="text-blue-600 hover:text-blue-900 mr-4"
                            >
                              编辑
                            </button>
                            <button
                              onClick={() => handleDelete(milestone.id!)}
                              className="text-red-600 hover:text-red-900"
                            >
                              删除
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">
                {editingId ? '编辑里程碑' : '添加里程碑'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="ri-close-line text-2xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Year */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  年份 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                  placeholder="例如: 2024"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Chinese Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  中文标题 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title_zh}
                  onChange={(e) => setFormData({ ...formData, title_zh: e.target.value })}
                  placeholder="例如: 成功举办首届GTRA峰会"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              {/* English Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">英文标题</label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  placeholder="例如: Successfully hosted the first GTRA Summit"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Chinese Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">中文描述</label>
                <textarea
                  value={formData.desc_zh}
                  onChange={(e) => setFormData({ ...formData, desc_zh: e.target.value })}
                  placeholder="详细描述这个里程碑..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* English Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">英文描述</label>
                <textarea
                  value={formData.desc_en}
                  onChange={(e) => setFormData({ ...formData, desc_en: e.target.value })}
                  placeholder="Describe this milestone in detail..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Icon Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">选择图标</label>
                <div className="grid grid-cols-5 gap-2">
                  {ICON_OPTIONS.map((icon) => (
                    <button
                      key={icon}
                      type="button"
                      onClick={() => setFormData({ ...formData, icon })}
                      className={`p-3 border-2 rounded-lg transition-all ${
                        formData.icon === icon
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <i className={`${icon} text-2xl block mx-auto`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Sort Order */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">排序权重</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <p className="mt-1 text-xs text-gray-500">数字越小排序越靠前</p>
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {saving ? '保存中...' : '保存'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
